import React, { useState, useEffect } from 'react';
import './SpotlightTab.css';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const CATEGORY_LABELS = {
  'news': { label: 'News', emoji: '📰', color: '#ef4444' },
  'politics': { label: 'Politics', emoji: '🏛️', color: '#8b5cf6' },
  'culture': { label: 'Culture', emoji: '🎭', color: '#ec4899' },
  'nature': { label: 'Nature', emoji: '🌿', color: '#10b981' },
  'science': { label: 'Science', emoji: '🔬', color: '#3b82f6' },
  'food_wine': { label: 'Food & Wine', emoji: '🍷', color: '#f59e0b' },
  'sports_leisure': { label: 'Sports', emoji: '⚔️', color: '#06b6d4' },
  'shakespeare': { label: 'Shakespeare', emoji: '📜', color: '#fbbf24' }
};

const SpotlightTab = ({ showToast }) => {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState('');
  const [sortBy, setSortBy] = useState('quality');
  const [removingOpps, setRemovingOpps] = useState({}); // opp_id -> 'left' | 'right'
  const [processingOpps, setProcessingOpps] = useState({}); // opp_id -> true

  const fetchOpportunities = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const adminToken = localStorage.getItem('admin_jwt');
      
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      params.append('sort_by', sortBy);
      params.append('limit', '25');
      
      const url = `${BACKEND_URL}/api/admin/spotlight?${params}`;
      console.log('Fetching Spotlight with params:', { category, sortBy, url });
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Spotlight response:', { count: data.count, opportunities: data.opportunities?.length });
        setOpportunities(data.opportunities || []);
      } else if (response.status === 401) {
        const errorData = await response.json().catch(() => ({}));
        setError(`Authentication required: ${errorData.detail || 'Invalid token'}`);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Spotlight fetch error:', { status: response.status, error: errorData });
        setError(`Failed to load: ${errorData.detail || response.statusText || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error fetching spotlight opportunities:', err);
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOpportunities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, sortBy]);

  const handleAccept = async (opp) => {
    if (processingOpps[opp.id]) return;

    setProcessingOpps(prev => ({ ...prev, [opp.id]: true }));

    try {
      const adminToken = localStorage.getItem('admin_jwt');

      // Trigger slide-right animation immediately for responsiveness
      setRemovingOpps(prev => ({ ...prev, [opp.id]: 'right' }));

      // Direct generate: bypasses Discord, creates scenes row for tracking
      const response = await fetch(`${BACKEND_URL}/api/admin/spotlight/opportunities/${opp.id}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const postUrl = data.bsky_web_url;
        if (postUrl) {
          showToast && showToast(`Posted — ${postUrl}`);
        }

        setTimeout(() => {
          setOpportunities(prev => prev.filter(o => o.id !== opp.id));
          setRemovingOpps(prev => {
            const copy = { ...prev };
            delete copy[opp.id];
            return copy;
          });
          setProcessingOpps(prev => {
            const copy = { ...prev };
            delete copy[opp.id];
            return copy;
          });
        }, 400);
      } else {
        const data = await response.json().catch(() => ({}));
        showToast && showToast(`Error: ${data.detail || 'Failed to generate scene'}`);
        setRemovingOpps(prev => {
          const copy = { ...prev };
          delete copy[opp.id];
          return copy;
        });
        setProcessingOpps(prev => {
          const copy = { ...prev };
          delete copy[opp.id];
          return copy;
        });
      }
    } catch (err) {
      console.error('Error generating spotlight scene:', err);
      showToast && showToast('Error: Scene generation failed');
      setRemovingOpps(prev => {
        const copy = { ...prev };
        delete copy[opp.id];
        return copy;
      });
      setProcessingOpps(prev => {
        const copy = { ...prev };
        delete copy[opp.id];
        return copy;
      });
    }
  };

  const handleReject = async (opp) => {
    if (processingOpps[opp.id]) return;
    
    setProcessingOpps(prev => ({ ...prev, [opp.id]: true }));
    
    try {
      const adminToken = localStorage.getItem('admin_jwt');
      
      // Trigger slide-left animation
      setRemovingOpps(prev => ({ ...prev, [opp.id]: 'left' }));
      
      // Mark as skipped in database
      const response = await fetch(`${BACKEND_URL}/api/admin/spotlight/opportunities/${opp.id}/skip`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        }
      });
      
      if (response.ok) {
        // Success: slide-away animation is sufficient feedback
        
        // Wait for animation, then remove
        setTimeout(() => {
          setOpportunities(prev => prev.filter(o => o.id !== opp.id));
          setRemovingOpps(prev => {
            const copy = { ...prev };
            delete copy[opp.id];
            return copy;
          });
          setProcessingOpps(prev => {
            const copy = { ...prev };
            delete copy[opp.id];
            return copy;
          });
        }, 400);
      } else {
        const data = await response.json();
        showToast && showToast(`Error: ${data.detail || 'Failed to skip'}`);
        setRemovingOpps(prev => {
          const copy = { ...prev };
          delete copy[opp.id];
          return copy;
        });
        setProcessingOpps(prev => {
          const copy = { ...prev };
          delete copy[opp.id];
          return copy;
        });
      }
    } catch (err) {
      console.error('Error rejecting opportunity:', err);
      showToast && showToast('Error: Failed to skip opportunity');
      setRemovingOpps(prev => {
        const copy = { ...prev };
        delete copy[opp.id];
        return copy;
      });
      setProcessingOpps(prev => {
        const copy = { ...prev };
        delete copy[opp.id];
        return copy;
      });
    }
  };

  if (loading) {
    return (
      <div className="spotlight-tab">
        <div className="spotlight-loading">
          <div className="spinner"></div>
          <p>Loading spotlight opportunities...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="spotlight-tab">
        <div className="spotlight-error">
          <p>⚠️ {error}</p>
          <button onClick={fetchOpportunities} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="spotlight-tab">
      {/* Header with filters */}
      <div className="spotlight-header">
        <div className="spotlight-title">
          <h2>🔦 Spotlight</h2>
          <p className="spotlight-subtitle">Curated high-reach posts for promote scenes</p>
        </div>
        
        <div className="spotlight-filters">
          <div className="filter-group">
            <label>Category:</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">All Categories</option>
              {Object.entries(CATEGORY_LABELS).map(([key, { label, emoji }]) => (
                <option key={key} value={key}>{emoji} {label}</option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label>Sort by:</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="quality">⭐ Quality</option>
              <option value="virality">🔥 Virality</option>
              <option value="recent">🕐 Recent</option>
            </select>
          </div>
          
          <button onClick={fetchOpportunities} className="refresh-button">
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Opportunities list */}
      {opportunities.length === 0 ? (
        <div className="spotlight-empty">
          <p>No {category ? `${CATEGORY_LABELS[category]?.label || category}` : 'spotlight'} opportunities found.</p>
          <p className="empty-hint">
            {category 
              ? `The scanner hasn't found recent high-quality posts in this category. Try "All Categories" or check back later.`
              : `The scanner is running but hasn't found posts meeting quality thresholds (Scene-Worthiness ≥ 7.0). Check back in 30 minutes.`
            }
          </p>
        </div>
      ) : (
        <div className="spotlight-opportunities">
          {opportunities.map(opp => {
            const categoryInfo = CATEGORY_LABELS[opp.category] || { label: opp.category, emoji: '📌', color: '#666' };
            const isRemoving = removingOpps[opp.id];
            const isProcessing = processingOpps[opp.id];
            
            return (
              <div 
                key={opp.id} 
                className={`spotlight-card ${isRemoving ? `removing-${isRemoving}` : ''}`}
              >
                {/* Category badge + age */}
                <div className="spotlight-card-header">
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span 
                      className="category-badge" 
                      style={{ backgroundColor: categoryInfo.color }}
                    >
                      {categoryInfo.emoji} {categoryInfo.label}
                    </span>
                    {opp.minutes_since_post != null && (
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '11px',
                        fontWeight: 600,
                        color: '#fff',
                        background: opp.minutes_since_post < 60 ? '#4caf50' :
                                    opp.minutes_since_post < 180 ? '#ff9800' :
                                    opp.minutes_since_post < 360 ? '#f44336' : '#880e4f'
                      }}>
                        {opp.age_display || `${opp.minutes_since_post}m`}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                    <span className="author-handle">@{opp.author_handle}</span>
                    {opp.post_url && (
                      <a 
                        href={opp.post_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ 
                          fontSize: '11px', 
                          color: '#4a9eff', 
                          textDecoration: 'none',
                          wordBreak: 'break-all'
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        🔗 View on Bluesky
                      </a>
                    )}
                  </div>
                </div>

                {/* Post content */}
                <div className="spotlight-content">
                  <p className="post-text">{opp.post_text}</p>
                </div>

                {/* Embedded images */}
                {opp.embed?.images && opp.embed.images.length > 0 && (
                  <div style={{
                    display: 'flex', gap: '8px', flexWrap: 'wrap',
                    marginBottom: '12px'
                  }}>
                    {opp.embed.images.map((img, idx) => (
                      <img
                        key={idx}
                        src={img.thumb || img.fullsize}
                        alt={img.alt || 'Embedded image'}
                        style={{
                          maxWidth: opp.embed.images.length === 1 ? '100%' : '48%',
                          maxHeight: '200px',
                          borderRadius: '8px',
                          objectFit: 'cover'
                        }}
                      />
                    ))}
                  </div>
                )}

                {/* Link preview */}
                {opp.embed?.external && (
                  <div style={{
                    border: '1px solid #333', borderRadius: '8px',
                    padding: '10px', marginBottom: '12px',
                    background: '#1a1a2e'
                  }}>
                    {opp.embed.external.title && (
                      <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '4px' }}>
                        {opp.embed.external.title}
                      </div>
                    )}
                    {opp.embed.external.description && (
                      <div style={{ fontSize: '12px', opacity: 0.7 }}>
                        {opp.embed.external.description.length > 200
                          ? opp.embed.external.description.slice(0, 200) + '...'
                          : opp.embed.external.description}
                      </div>
                    )}
                    {opp.embed.external.uri && (
                      <a
                        href={opp.embed.external.uri}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: '11px', color: '#4a9eff', textDecoration: 'none' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        🔗 {new URL(opp.embed.external.uri).hostname}
                      </a>
                    )}
                  </div>
                )}

                {/* LLM Reasoning */}
                {opp.scene_worthiness_reasoning && (
                  <div className="llm-reasoning" style={{
                    fontSize: '13px',
                    fontStyle: 'italic',
                    color: '#4a9eff',
                    padding: '8px 12px',
                    backgroundColor: 'rgba(74, 158, 255, 0.1)',
                    borderRadius: '6px',
                    marginBottom: '12px'
                  }}>
                    💭 {opp.scene_worthiness_reasoning}
                  </div>
                )}

                {/* Metrics */}
                <div className="spotlight-metrics">
                  <div className="metric">
                    <span className="metric-label">Scene-Worthiness:</span>
                    <span className="metric-value" style={{fontWeight: 'bold', color: '#4a9eff'}}>
                      🎭 {opp.scene_worthiness_score || 0}
                    </span>
                  </div>
                  <div className="metric">
                    <span className="metric-label">Quality:</span>
                    <span className="metric-value quality">⭐ {opp.quality_score}</span>
                  </div>
                  <div className="metric">
                    <span className="metric-label">Virality:</span>
                    <span className="metric-value virality">🔥 {opp.virality_score}</span>
                  </div>
                  <div className="metric">
                    <span className="metric-label">Engagement:</span>
                    <span className="metric-value engagement">
                      💙 {opp.like_count} · 🔁 {opp.repost_count} · 💬 {opp.reply_count}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="spotlight-actions">
                  <button
                    onClick={() => handleReject(opp)}
                    disabled={isProcessing}
                    className="reject-button"
                  >
                    ✗ Reject
                  </button>
                  
                  <a
                    href={opp.post_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="view-button"
                  >
                    🔗 View Post
                  </a>
                  
                  <button
                    onClick={() => handleAccept(opp)}
                    disabled={isProcessing}
                    className="accept-button"
                  >
                    ✓ Accept & Promote
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SpotlightTab;

